/**
 * Directional hover fill.
 *
 * ONE pair of delegated listeners on the document, not a listener per button.
 * That is what makes dynamically rendered cards, cart re-renders and theme
 * editor section reloads work with no duplicate handlers and nothing to
 * re-bind: pointerover and pointerout bubble, pointerenter and pointerleave
 * do not, which is why those are the two used here.
 *
 * The element keeps its own click behaviour untouched. Nothing here calls
 * preventDefault, and the moving layer is a pseudo-element, so a link is still
 * a link and a submit button still submits.
 */
(function () {
  "use strict";
  if (window.__calmyDirFill) return;
  window.__calmyDirFill = true;

  var SEL = ".cdir";
  var MS = 300;

  /* Fine pointers only. On a touch screen a hover effect either never fires or
     sticks after a tap, and CSS already gives those devices an :active state. */
  var fine = window.matchMedia("(hover: hover) and (pointer: fine)");

  /* WHICH EDGE, normalised by the button's own dimensions.

     Comparing raw pixel distances would make a 280x46 pill claim almost every
     entry as "left" or "right", because the vertical distances are tiny by
     comparison. Dividing by width and height first puts both axes on the same
     0..1 scale, so a wide short button answers the question about its own
     shape rather than about its aspect ratio. */
  function edge(el, x, y) {
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return "top";
    var nx = (x - r.left) / r.width - 0.5;
    var ny = (y - r.top) / r.height - 0.5;
    if (Math.abs(nx) > Math.abs(ny)) return nx < 0 ? "left" : "right";
    return ny < 0 ? "top" : "bottom";
  }

  function park(el, side) {
    var x = "0%", y = "0%";
    if (side === "left") x = "-101%";
    else if (side === "right") x = "101%";
    else if (side === "top") y = "-101%";
    else y = "101%";
    el.style.setProperty("--cdir-x", x);
    el.style.setProperty("--cdir-y", y);
  }

  function enter(el, x, y) {
    var now = Date.now();
    /* FAST RE-ENTRY. If the layer is still most of the way across from a
       previous exit, snapping it to the new entry edge would flash. So it only
       snaps once the exit is substantially done; before that it simply slides
       back in from wherever it currently is, which is what the eye expects
       when someone waggles the pointer over a button. */
    var out = el.__cdirOutAt ? (now - el.__cdirOutAt) / MS : 1;
    if (out >= 0.55) {
      el.classList.add("is-cdir-jump");
      park(el, edge(el, x, y));
      /* One forced reflow so the parked position is committed before the
         transition is switched back on. Without it the browser coalesces both
         writes and the layer animates across the button instead of appearing
         at its edge. */
      void el.offsetWidth;
      el.classList.remove("is-cdir-jump");
    }
    el.classList.add("is-cdir-in");
  }

  function leave(el, x, y) {
    el.__cdirOutAt = Date.now();
    /* Out through the edge actually being crossed, which is why this reads the
       pointer position at the moment of leaving rather than remembering the
       one it came in by. */
    park(el, edge(el, x, y));
    el.classList.remove("is-cdir-in");
  }

  document.addEventListener("pointerover", function (e) {
    if (!fine.matches || e.pointerType === "touch") return;
    var el = e.target.closest && e.target.closest(SEL);
    if (!el || el.disabled) return;
    /* pointerover also fires moving between children of the same button. */
    if (e.relatedTarget && el.contains(e.relatedTarget)) return;
    enter(el, e.clientX, e.clientY);
  });

  document.addEventListener("pointerout", function (e) {
    if (!fine.matches || e.pointerType === "touch") return;
    var el = e.target.closest && e.target.closest(SEL);
    if (!el) return;
    if (e.relatedTarget && el.contains(e.relatedTarget)) return;
    leave(el, e.clientX, e.clientY);
  });

  /* A button removed or re-rendered mid-hover leaves nothing behind: the class
     lives on the element that went away. Nothing to clean up. */
})();
